import { useState, useEffect, useRef } from 'react';
import socketManager from '../lib/socket';

/**
 * useVitals — shared hook for real-time vitals binding
 *
 * Subscribes to socket events from the iVivaCore telemetry backend and
 * returns the latest vitals payload for a given patient.
 *
 * Events handled:
 *   vitals_update        → patient's private room (ingestionService.js)
 *   patient_vitals_sync  → clinicians_room broadcast (ingestionService.js)
 *   vitals_sync          → wearableBridge.js broadcast
 *
 * @param {string|number} patientId  — the patient's ID to filter events for
 * @param {object}        fallback   — initial/fallback vitals when no live
 *                                    data has arrived yet
 * @returns {{ vitals, lastUpdated, isLive, source }}
 */
const useVitals = (patientId, fallback = {}) => {
  const [vitals, setVitals] = useState(fallback);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [source, setSource] = useState('fallback'); // 'fallback' | 'vitals_update' | 'patient_vitals_sync' | 'vitals_sync'

  // Keep patientId in a ref so event handlers always see the latest value
  // without needing to re-register listeners on every render
  const patientIdRef = useRef(patientId);
  useEffect(() => { patientIdRef.current = patientId; }, [patientId]);

  const getCacheKey = (id) => `latestVitals:${id}`;

  const hydrateFromCache = (id) => {
    if (!id) return false;

    try {
      const cached = JSON.parse(localStorage.getItem(getCacheKey(id)));
      if (cached?.vitals && cached?.receivedAt) {
        setVitals({ ...fallback, ...cached.vitals });
        setLastUpdated(new Date(cached.receivedAt));
        setIsLive(true);
        setSource(cached.source || 'cache');
        return true;
      }
    } catch (error) {
      console.warn('[Vitals] Unable to load cached vitals:', error.message);
    }

    return false;
  };

  useEffect(() => {
    if (!patientId) return;

    const handleCachedVitalsChange = (event) => {
      const changedPatientId =
        event.detail?.patientId ||
        event.key?.replace('latestVitals:', '');

      if (String(changedPatientId) === String(patientIdRef.current)) {
        hydrateFromCache(patientIdRef.current);
      }
    };

    window.addEventListener('storage', handleCachedVitalsChange);
    window.addEventListener('latestVitalsChanged', handleCachedVitalsChange);

    /**
     * Shared handler — all three events use the same payload shape
     * coming out of mapProviderToSchema() in wearableBridge.js:
     * { patientId, vitals: { hr, bp, temp, glucose, spo2, ... }, ... }
     *
     * We only apply the update if the event is for our patient.
     */
    const handleVitals = (eventName) => (data) => {
      // Guard: ignore events for other patients
      const incomingId = data?.patientId ?? data?.patient_id;
      if (String(incomingId) !== String(patientIdRef.current)) return;

      const incoming = data?.vitals ?? data;
      if (!incoming || typeof incoming !== 'object') return;

      setVitals((prev) => ({ ...prev, ...incoming }));
      const receivedAt = new Date();
      setLastUpdated(receivedAt);
      setIsLive(true);
      setSource(eventName);

      try {
        localStorage.setItem(
          getCacheKey(incomingId),
          JSON.stringify({ vitals: incoming, receivedAt: receivedAt.toISOString(), source: eventName }),
        );
      } catch (error) {
        console.warn('[Vitals] Unable to cache latest vitals:', error.message);
      }
    };

    const onVitalsUpdate       = handleVitals('vitals_update');
    const onPatientVitalsSync  = handleVitals('patient_vitals_sync');
    const onVitalsSync         = handleVitals('vitals_sync');

    socketManager.on('vitals_update',       onVitalsUpdate);
    socketManager.on('patient_vitals_sync', onPatientVitalsSync);
    socketManager.on('vitals_sync',         onVitalsSync);

    return () => {
      window.removeEventListener('storage', handleCachedVitalsChange);
      window.removeEventListener('latestVitalsChanged', handleCachedVitalsChange);
      socketManager.off('vitals_update',       onVitalsUpdate);
      socketManager.off('patient_vitals_sync', onPatientVitalsSync);
      socketManager.off('vitals_sync',         onVitalsSync);
    };
  }, [patientId]);

  // Reset to fallback when patient changes
  useEffect(() => {
    if (!patientId) {
      setVitals(fallback);
      setLastUpdated(null);
      setIsLive(false);
      setSource('fallback');
      return;
    }

    if (hydrateFromCache(patientId)) return;

    setVitals(fallback);
    setLastUpdated(null);
    setIsLive(false);
    setSource('fallback');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  return { vitals, lastUpdated, isLive, source };
};

export default useVitals;
